// ============================================================
// useCharacterSync - Shared character library sync with Firestore.
// ============================================================
// Subscribes to the `sessions/{id}/characters` subcollection,
// provides push/pull/delete actions, and prevents echo loops
// via per-character JSON comparison.
//
// Design: characters are *templates* - resolved once at spawn.
// No live binding, so snapshot-at-load semantics are sufficient.
// ============================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import type { MutableRefObject } from 'react';
import type { Character } from '../engine/character/types';
import type { CollectionRead } from '../backend/types';
import { normalizeCharacter } from '../engine/character/normalize';
import { toMs } from '../utils/timestamps';
import deepClone from '../utils/deepClone';

export interface SharedCharacterEntry {
  character: Character;
  name: string;
  updatedAt: number; // ms
  updatedBy: string;
  ownerUid: string;
}

export interface UseCharacterSyncReturn {
  sessionCharacters: SharedCharacterEntry[];
  synced: boolean;
  pushCharacter: (character: Character) => void;
  pushAllCharacters: (characters: Character[]) => void;
  deleteSessionCharacter: (id: string) => void;
  pullCharacter: (id: string) => Character | null;
}

/** Multiplayer service subset used by character sync (DI boundary). */
interface CharacterSyncMp {
  onCharactersUpdate: (
    sessionId: string,
    callback: (read: CollectionRead<Record<string, unknown>>) => void,
  ) => () => void;
  setSharedCharacter: (
    sessionId: string,
    characterId: string,
    character: Character,
    playerId: string | null,
  ) => Promise<void>;
  deleteSharedCharacter: (sessionId: string, characterId: string) => Promise<void>;
  batchSetSharedCharacters: (sessionId: string, characters: Character[], playerId: string | null) => Promise<void>;
}

interface Params {
  sessionId: string | null;
  playerId: string | null;
  mp: CharacterSyncMp;
}

type CharacterDoc = CollectionRead<Record<string, unknown>>['entries'][number];

const byName = (a: SharedCharacterEntry, b: SharedCharacterEntry) => a.name.localeCompare(b.name);

/**
 * One stored document as a library entry, with the raw string it came
 * from; null when it has no data. Normalized on read - legacy skill
 * shapes migrate - while the raw string stays untouched, because echo
 * prevention compares strings. Throws on a corrupt document.
 */
function parseCharacterDoc(docSnap: CharacterDoc): { entry: SharedCharacterEntry; dataStr: string } | null {
  const raw = docSnap.data;
  const dataStr = raw.data as string | null | undefined;
  if (!dataStr) return null;
  const character: Character = normalizeCharacter(JSON.parse(dataStr));
  const entry = {
    character,
    name: (raw.name as string) ?? character.name ?? 'Unknown',
    updatedAt: toMs(raw.updatedAt as number | null) || 0,
    updatedBy: (raw.updatedBy as string) ?? 'unknown',
    ownerUid: (raw.ownerUid as string) ?? 'unknown',
  };
  return { entry, dataStr };
}

/** Every readable document, by name, and the raw string cached per id. Corrupt ones are skipped - others still load. */
function parseCharacterDocs(docs: CharacterDoc[]) {
  const entries: SharedCharacterEntry[] = [];
  const cache: Record<string, string> = {};
  for (const docSnap of docs) {
    try {
      const parsed = parseCharacterDoc(docSnap);
      if (!parsed) continue;
      cache[docSnap.id] = parsed.dataStr;
      entries.push(parsed.entry);
    } catch (err) {
      console.error('[CharacterSync] Failed to parse character doc:', docSnap.id, err);
    }
  }
  return { entries: entries.sort(byName), cache };
}

/** What the library last showed, and the raw strings behind it. */
interface ShownLibrary {
  entries: SharedCharacterEntry[];
  cache: Record<string, string>;
}

/**
 * A SNAPSHOT TAKEN MID-BURST LISTS ONLY SOME OF THEM. Importing a folder
 * writes twenty documents, and each write brings back a view with the
 * rest still missing - so the library flickered through 3, 11, 20, and
 * any partial view that landed last left characters simply absent from
 * the menu. Only a settled view may say somebody is gone; a partial one
 * keeps what was shown unless the read names it removed (v8.25.0).
 * `removed` is the read's, not the snapshot's: nothing above the backend
 * port names docChanges(). Adds the kept entries' strings to `next.cache`.
 */
function keepUnlisted(next: ShownLibrary, last: ShownLibrary, read: CollectionRead<unknown>): SharedCharacterEntry[] {
  if (read.listsEveryone) return next.entries;
  const removed = new Set(read.removed);
  const seen = new Set(next.entries.map((e) => e.character.id));
  const kept = last.entries.filter((e) => !seen.has(e.character.id) && !removed.has(e.character.id));
  if (!kept.length) return next.entries;
  for (const e of kept) next.cache[e.character.id] = last.cache[e.character.id];
  return [...next.entries, ...kept].sort(byName);
}

/** Echo prevention: the whole collection's raw strings, in id order. */
const cachedJson = (cache: Record<string, string>) =>
  JSON.stringify(
    Object.keys(cache)
      .sort()
      .map((id) => cache[id]),
  );

/**
 * The library a read leaves on screen, or null when it matches what is
 * already shown (an echo). Updates `shownRef` when it changes.
 */
function libraryAfterRead(shownRef: MutableRefObject<ShownLibrary>, read: CollectionRead<Record<string, unknown>>) {
  const next = parseCharacterDocs(read.entries);
  const oldJson = cachedJson(shownRef.current.cache);
  const merged = keepUnlisted(next, shownRef.current, read);
  if (JSON.stringify(merged.map((e) => next.cache[e.character.id])) === oldJson) return null;
  shownRef.current = { entries: merged, cache: next.cache };
  return merged;
}

/** The collection listener. `synced` is set after the first view - even an empty one. */
function useCharactersListener({ sessionId, mp }: Params) {
  const [sessionCharacters, setSessionCharacters] = useState<SharedCharacterEntry[]>([]);
  const [synced, setSynced] = useState(false);
  /** What we last showed - the only way to keep an in-flight write visible. */
  const shownRef = useRef<ShownLibrary>({ entries: [], cache: {} });
  useEffect(() => {
    if (!sessionId) {
      setSessionCharacters([]);
      setSynced(false);
      shownRef.current = { entries: [], cache: {} };
      return;
    }
    const unsub = mp.onCharactersUpdate(sessionId, (read) => {
      const merged = libraryAfterRead(shownRef, read);
      if (merged) setSessionCharacters(merged);
      setSynced(true);
    });
    return () => unsub();
  }, [sessionId, mp]);
  return { sessionCharacters, synced };
}

/** Log a failed write under its verb; the library is templates, so nothing else waits on it. */
const logFailure = (what: string) => (err: unknown) => {
  console.error(`[CharacterSync] ${what} failed:`, err);
};

/** Push one, push many, delete one - each a no-op outside a session. */
function useCharacterWriters({ sessionId, playerId, mp }: Params) {
  const pushCharacter = useCallback(
    (character: Character) => {
      if (sessionId) mp.setSharedCharacter(sessionId, character.id, character, playerId).catch(logFailure('Push'));
    },
    [sessionId, playerId, mp],
  );
  const pushAllCharacters = useCallback(
    (characters: Character[]) => {
      if (sessionId) mp.batchSetSharedCharacters(sessionId, characters, playerId).catch(logFailure('Batch push'));
    },
    [sessionId, playerId, mp],
  );
  const deleteSessionCharacter = useCallback(
    (id: string) => {
      if (sessionId) mp.deleteSharedCharacter(sessionId, id).catch(logFailure('Delete'));
    },
    [sessionId, mp],
  );
  return { pushCharacter, pushAllCharacters, deleteSessionCharacter };
}

/** The shared character library: templates, resolved once at spawn. */
export default function useCharacterSync(params: Params): UseCharacterSyncReturn {
  const { sessionCharacters, synced } = useCharactersListener(params);
  // A deep clone, so a spawned character never shares an object with the library.
  const pullCharacter = useCallback(
    (id: string): Character | null => {
      const entry = sessionCharacters.find((e) => e.character.id === id);
      return entry ? deepClone(entry.character) : null;
    },
    [sessionCharacters],
  );
  return { sessionCharacters, synced, ...useCharacterWriters(params), pullCharacter };
}
